const ACCEPTED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp"];
const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp"];

export const SIGNATURE_UPLOAD_ACCEPT = ACCEPTED_MIME_TYPES.join(",");

export const isAcceptedSignatureImageFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const hasAcceptedExtension = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  const hasAcceptedMimeType = !file.type || ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase());
  return hasAcceptedExtension && hasAcceptedMimeType;
};

/**
 * Normalizes any accepted upload format (jpg/jpeg/png/gif/bmp) to a PNG data
 * URL by rasterizing it on an offscreen canvas, so a saved signature always
 * flows through the same `<Image src="data:image/png;base64,...">` PDF
 * insertion path as a canvas-drawn signature.
 */
export const imageFileToPngDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!isAcceptedSignatureImageFile(file)) {
      reject(new Error("Unsupported file type. Please upload a jpg, jpeg, png, gif or bmp image."));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error("Could not process the selected image."));
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx || canvas.width === 0 || canvas.height === 0) {
          reject(new Error("Could not process the selected image."));
          return;
        }

        ctx.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
};
